import { Box, Typography } from "@mui/material";

const TIERS = {
  high: "#00BB7E",
  mid: "#FF925D",
  low: "#999999",
} as const;

function getTierColor(percent: number) {
  if (percent >= 90) return TIERS.high;
  if (percent >= 70) return TIERS.mid;
  return TIERS.low;
}

/**
 * Converts a CIEDE2000 ΔE color distance into a 0–100 "match percentage"
 * suitable for display.
 *
 * Uses the simple affine mapping `percent = 100 - ΔE`, clamped to `[0, 100]`
 * and rounded to the nearest integer. A perfect match (ΔE = 0) renders as
 * 100%; anything with ΔE ≥ 100 renders as 0%. Negative ΔE inputs (which
 * shouldn't occur in practice) are clamped to 100%.
 *
 * The mapping is deliberately coarse — it's a UI affordance, not a
 * perceptual model.
 */
export function deltaEToPercent(deltaE: number): number {
  return Math.round(Math.max(0, Math.min(100, 100 - deltaE)));
}

interface MatchPercentBadgeProps {
  deltaE: number;
}

export const MatchPercentBadge = ({ deltaE }: MatchPercentBadgeProps) => {
  const percent = deltaEToPercent(deltaE);
  const color = getTierColor(percent);

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        px: "8.5px",
        py: "1.5px",
        borderRadius: "9999px",
        border: `1.5px solid ${color}`,
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          width: 5,
          height: 5,
          borderRadius: "2.5px",
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 500,
          lineHeight: "15px",
          color,
          whiteSpace: "nowrap",
        }}
      >
        {percent}%
      </Typography>
    </Box>
  );
};
